import type { TContentBlockBase, TError, TLessonBase } from '@onesource/schemas';
import { useQuery } from '@tanstack/react-query';

import axios from '../../axios';
import { ENTITY } from '../entity';

/**
 * Lesson with content blocks
 */
export interface TLessonWithBlocks extends TLessonBase {
  contentBlocks: TContentBlockBase[];
}

/**
 * Query key for fetching a specific lesson by ID - non-hook version
 */
export const getLessonQueryKey = (id: string) => [ENTITY, 'lesson', id];

/**
 * Query key for fetching a specific lesson by ID
 */
export const useGetLessonQueryKey = (id: string) => getLessonQueryKey(id);

interface UseGetLessonOptions {
  enabled?: boolean;
}

/**
 * Hook to fetch a specific lesson by ID with its content blocks
 *
 * @param id - Lesson ID (UUID)
 * @param options - Query options (enabled, etc.)
 */
export const useGetLesson = (id: string, options?: UseGetLessonOptions) => {
  const enabled = options?.enabled ?? true;

  return useQuery<TLessonWithBlocks, TError>({
    queryKey: useGetLessonQueryKey(id),
    queryFn: async () => {
      const res = await axios.get<TLessonWithBlocks>(`/community-hub/lessons/${id}`);
      return res.data;
    },
    enabled: !!id && enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};
